import { InfiniteScroll } from "@/components/shared/infinite-scroll";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
  InAppNotification,
  NotificationType,
  useNotificationEvents,
  useNotifications,
} from "@/lib/api/notifications";
import { useFeatureFlagEnabled } from "@posthog/react";
import { useCurrentUser, UserRole } from "@/lib/api/user";
import { useInboxStore } from "@/lib/hooks/use-inbox-store";
import { cn } from "@/lib/utils";
import { ChevronDoubleLeftIcon } from "@heroicons/react/24/outline";
import { Filters } from "@somethings-ui/web";
import { useRouterState } from "@tanstack/react-router";
import {
  isAfter,
  parseISO,
  startOfMonth,
  startOfToday,
  startOfWeek,
  startOfYesterday,
  subMonths,
  subWeeks,
} from "date-fns";
import { InboxIcon, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { NotificationItem } from "./notification-item";
import { useMentorView } from "@/lib/hooks/admin/use-mentor-view";
import { useCurrentConversation } from "@/lib/hooks/conversations/use-current-conversation";
import { getUserTimezoneWithDefault } from "@/lib/utils/timezone";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";

// Utility function to group notifications by time period
const groupNotifications = (notifications: InAppNotification[]) => {
  const today = startOfToday();
  const yesterday = startOfYesterday();
  const thisWeekStart = startOfWeek(new Date());
  const lastWeekStart = subWeeks(thisWeekStart, 1);
  const thisMonthStart = startOfMonth(new Date());
  const lastMonthStart = subMonths(thisMonthStart, 1);

  const groups: Record<string, InAppNotification[]> = {
    Today: [],
    Yesterday: [],
    "This Week": [],
    "Last Week": [],
    "This Month": [],
    "Last Month": [],
    Rest: [],
  };

  notifications.forEach((notification) => {
    const date = parseISO(notification.created_at);

    if (isAfter(date, today)) {
      groups["Today"].push(notification);
    } else if (isAfter(date, yesterday)) {
      groups["Yesterday"].push(notification);
    } else if (isAfter(date, thisWeekStart)) {
      groups["This Week"].push(notification);
    } else if (isAfter(date, lastWeekStart)) {
      groups["Last Week"].push(notification);
    } else if (isAfter(date, thisMonthStart)) {
      groups["This Month"].push(notification);
    } else if (isAfter(date, lastMonthStart)) {
      groups["Last Month"].push(notification);
    } else {
      groups["Rest"].push(notification);
    }
  });

  return groups;
};

interface InboxInnerProps {
  isOpen: boolean;
  close: () => void;
  isAdmin?: boolean;
  userId?: string;
  teenId?: string;
}

const InboxInner = ({
  isOpen,
  close,
  isAdmin,
  userId,
  teenId,
}: InboxInnerProps) => {
  const router = useRouterState();
  const { mentorView } = useMentorView();
  const [selectedFilter, setSelectedFilter] = useState<
    "all" | "current-teen" | "unread"
  >("unread");
  const { track } = useTrackEvent();

  useEffect(() => {
    if (isOpen) {
      track(PostHogEvents.TODO_VIEWED);
    }
  }, [isOpen, track]);

  const { data: user } = useCurrentUser();
  const isMomentsEnabled = useFeatureFlagEnabled("enable-moments");
  const isSwapsEnabled = useFeatureFlagEnabled("enable-swaps");

  const {
    data: notificationsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useNotifications({
    userId: isAdmin ? userId : undefined,
    menteeId: selectedFilter === "current-teen" ? teenId : undefined,
    unreadOnly: selectedFilter === "unread",
  });

  const notifications = useMemo(() => {
    const allNotifications =
      notificationsData?.pages.flatMap((page) => page.notifications) ?? [];

    // Filter out moment/swap notifications if feature flag is off
    return allNotifications.filter((n) => {
      if (n.type !== NotificationType.SHAREABLE_ENTITY_SHARED) return true;
      const data = n.data as { sharedEntityType?: string } | undefined;
      const entityType = data?.sharedEntityType?.toLowerCase();
      if (!isMomentsEnabled && entityType === "moment") return false;
      if (!isSwapsEnabled && entityType === "swap") return false;
      return true;
    });
  }, [notificationsData, isMomentsEnabled, isSwapsEnabled]);

  const groupedNotifications = groupNotifications(notifications);

  const shouldShowCurrentTeenFilter = useMemo(() => {
    if (isAdmin && router.location.pathname.match(/\/mentorships\/.+$/)) {
      return true;
    }

    if (
      !isAdmin &&
      mentorView &&
      router.location.pathname === "/conversations"
    ) {
      return true;
    }

    return false;
  }, [isAdmin, router.location.pathname, mentorView]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <>
          {Array.from({ length: 3 }).map((_, groupIndex) => (
            <div key={groupIndex} className="space-y-1">
              <div className="bg-muted/60 sticky top-0 px-4 py-2">
                <Skeleton className="h-5 w-24" />
              </div>
              {Array.from({ length: 3 }).map((_, itemIndex) => (
                <div key={itemIndex} className="px-4 py-2">
                  <Skeleton className="h-16 w-full rounded-lg" />
                </div>
              ))}
            </div>
          ))}
        </>
      );
    }

    if (notifications.length === 0) {
      return (
        <div className="flex h-[calc(100vh-200px)] flex-col items-center justify-center text-center">
          <InboxIcon className="text-muted-foreground/50 h-12 w-12" />
          <h3 className="text-muted-foreground mt-4 text-lg font-medium">
            No notifications
          </h3>
          <p className="text-muted-foreground/75 mt-2 text-sm">
            You&apos;re all caught up! New notifications will appear here.
          </p>
        </div>
      );
    }

    return (
      <>
        {Object.entries(groupedNotifications).map(([period, items]) => {
          if (items.length === 0) return null;

          return (
            <div key={period} className="space-y-1">
              <div className="bg-muted/60 text-muted-foreground sticky top-0 px-4 py-2 text-sm font-medium backdrop-blur-sm">
                {period}
              </div>
              {items.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  isAdmin={isAdmin}
                  setClose={close}
                  timezone={getUserTimezoneWithDefault(user)}
                />
              ))}
            </div>
          );
        })}
        <InfiniteScroll
          hasMore={hasNextPage}
          isLoading={isFetchingNextPage}
          onLoadMore={() => fetchNextPage()}
          className="h-4"
        />
        {isFetchingNextPage && (
          <div className="flex justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}
      </>
    );
  };

  const filters = useMemo(() => {
    const allFilters = [
      {
        label: "Unread",
        onClick: () => {
          setSelectedFilter("unread");
        },
        isActive: selectedFilter === "unread",
      },
      {
        label: "All",
        onClick: () => {
          setSelectedFilter("all");
        },
        isActive: selectedFilter === "all",
      },
    ];

    if (shouldShowCurrentTeenFilter) {
      allFilters.push({
        label: "Current Teen",
        onClick: () => {
          setSelectedFilter("current-teen");
        },
        isActive: selectedFilter === "current-teen",
      });
    }

    return allFilters;
  }, [selectedFilter, shouldShowCurrentTeenFilter]);

  return (
    <SheetContent
      side="left"
      className={cn(
        "!focus-visible:ring-0 w-[400px] border-r p-0 sm:w-[540px]",
        isOpen && "left-[96px]",
      )}
      hideCloseButton
    >
      <SheetHeader className="flex flex-row items-center justify-between space-y-0 border-b bg-white p-4">
        <SheetTitle className="flex-1 font-heading font-bold text-black">
          Todos
        </SheetTitle>
        <button
          onClick={close}
          className="flex flex-shrink-0 items-center justify-center"
        >
          <ChevronDoubleLeftIcon className="h-5 w-5" />
        </button>
      </SheetHeader>
      <Filters items={filters} />

      <div
        className="overflow-y-auto"
        style={{
          height: shouldShowCurrentTeenFilter
            ? "calc(100vh - 100px)"
            : "calc(100vh - 60px)",
          overscrollBehavior: "contain",
        }}
      >
        {renderContent()}
      </div>
    </SheetContent>
  );
};

export const InboxSidebar = () => {
  useNotificationEvents();
  const { isOpen, close } = useInboxStore();
  const currentTeenId = useCurrentConversation((state) => state.currentTeenId);

  return (
    <Sheet open={isOpen} onOpenChange={close} modal={false}>
      <InboxInner
        isOpen={isOpen}
        close={close}
        teenId={currentTeenId || undefined}
      />
    </Sheet>
  );
};

interface InboxProps {
  userId: string;
  teenId?: string;
  isOpen: boolean;
  close: () => void;
}

export const Inbox = ({ userId, teenId, isOpen, close }: InboxProps) => {
  const { data: user } = useCurrentUser();

  if (!user) return null;

  return (
    <Sheet open={isOpen} onOpenChange={close} modal={false}>
      <InboxInner
        isOpen={isOpen}
        close={close}
        isAdmin={
          user.role === UserRole.ADMIN || user.role === UserRole.SUPERADMIN
        }
        userId={userId}
        teenId={teenId}
      />
    </Sheet>
  );
};
